import * as React from "react"

import { cn } from "@/lib/utils"
import { Select } from "@/components/ui/Select"

type TextFieldVariant = "outlined" | "filled" | "standard"
type TextFieldSize = "small" | "medium"

type InputAdornmentProps = {
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
}

export interface TextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  children?: React.ReactNode
  helperText?: React.ReactNode
  error?: boolean
  fullWidth?: boolean
  label?: React.ReactNode
  margin?: "none" | "dense" | "normal"
  multiline?: boolean
  rows?: number
  minRows?: number
  maxRows?: number
  select?: boolean
  size?: TextFieldSize
  sx?: unknown
  variant?: TextFieldVariant
  InputProps?: InputAdornmentProps & Record<string, unknown>
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> &
    React.TextareaHTMLAttributes<HTMLTextAreaElement>
  InputLabelProps?: {
    shrink?: boolean
  } & Record<string, unknown>
  FormHelperTextProps?: React.HTMLAttributes<HTMLParagraphElement>
}

function getWrapperClasses(variant: TextFieldVariant, error?: boolean, disabled?: boolean) {
  const accent = error
    ? "focus-within:border-[var(--error)] focus-within:ring-[color-mix(in_srgb,var(--error)_15%,transparent)]"
    : "focus-within:border-[var(--info)] focus-within:ring-[color-mix(in_srgb,var(--info)_15%,transparent)]"

  if (variant === "standard") {
    return cn(
      "rounded-none border-x-0 border-t-0 border-b bg-transparent px-0 shadow-none",
      error ? "border-[var(--error-border)]" : "border-[var(--border-default)]",
      accent,
      disabled && "opacity-60"
    )
  }

  if (variant === "filled") {
    return cn(
      "border-transparent bg-[var(--bg-subtle)] shadow-[inset_0_1px_0_color-mix(in_srgb,var(--text-inverse)_8%,transparent)]",
      error ? "focus-within:bg-[var(--error-light)]" : "focus-within:bg-[var(--control-bg)]",
      accent,
      disabled && "opacity-60"
    )
  }

  return cn(
    "border bg-[var(--control-bg)] shadow-[var(--shadow-md),inset_0_1px_0_color-mix(in_srgb,var(--text-inverse)_8%,transparent)]",
    error ? "border-[var(--error-border)]" : "border-[var(--border-default)]",
    accent,
    disabled && "opacity-60"
  )
}

function getInputPadding(
  size: TextFieldSize,
  hasLabel: boolean,
  hasStartAdornment: boolean,
  hasEndAdornment: boolean,
  multiline?: boolean
) {
  const topPadding = hasLabel ? (size === "small" ? "pt-5" : "pt-6") : size === "small" ? "pt-2.5" : "pt-3.5"
  const bottomPadding = multiline ? "pb-3" : size === "small" ? "pb-2.5" : "pb-3.5"
  const leftPadding = hasStartAdornment ? "pl-11" : "pl-4"
  const rightPadding = hasEndAdornment ? "pr-11" : "pr-4"

  return cn(topPadding, bottomPadding, leftPadding, rightPadding)
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      children,
      className,
      disabled,
      error,
      FormHelperTextProps,
      fullWidth,
      helperText,
      id,
      InputLabelProps,
      inputProps,
      InputProps,
      label,
      margin = "none",
      maxRows,
      minRows,
      multiline,
      name,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      required,
      rows,
      select,
      size = "medium",
      style,
      type = "text",
      value,
      variant = "outlined",
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = React.useState(false)
    const fieldId = React.useId()
    const inputId = id ?? `${name ?? "text-field"}-${fieldId}`
    const hasValue = Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== ""
    const shouldShrink = Boolean(InputLabelProps?.shrink || focused || hasValue || placeholder)
    const hasStartAdornment = Boolean(InputProps?.startAdornment)
    const hasEndAdornment = Boolean(InputProps?.endAdornment)

    const marginClass =
      margin === "normal" ? "my-4" : margin === "dense" ? "my-2" : ""

    const sharedFieldClasses = cn(
      "relative flex items-center overflow-hidden rounded-[20px] transition-all duration-200 focus-within:ring-4",
      getWrapperClasses(variant, error, disabled),
      fullWidth && "w-full",
      className
    )

    const labelClasses = cn(
      "pointer-events-none absolute left-4 top-1/2 z-10 origin-left -translate-y-1/2 text-[var(--text-muted)] transition-all duration-200",
      hasStartAdornment && "left-11",
      shouldShrink && "top-3 translate-y-0 scale-[0.82] text-[var(--control-placeholder)]",
      error && "text-[var(--text-danger)]",
      focused && !error && "text-[var(--info)]"
    )

    if (select) {
      return (
        <div className={cn("flex flex-col", marginClass, fullWidth && "w-full")} style={style}>
          {label && (
            <label htmlFor={inputId} className="mb-2 text-sm font-medium text-[var(--text-secondary)]">
              {label}
              {required ? " *" : ""}
            </label>
          )}

          <Select
            id={inputId}
            name={name}
            value={value as string | number | string[] | undefined}
            onChange={onChange as ((event: { target: { name?: string; value: string | number | string[] | undefined } }) => void) | undefined}
            disabled={disabled}
            fullWidth={fullWidth}
            variant={variant}
            displayEmpty
            inputProps={inputProps}
            className={cn(error && "text-[var(--text-danger)]")}
            style={style}
            {...(props as Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">)}
          >
            {children}
          </Select>

          {helperText && (
            <p
              className={cn("mt-1.5 text-xs", error ? "text-[var(--text-danger)]" : "text-[var(--text-muted)]")}
              {...FormHelperTextProps}
            >
              {helperText}
            </p>
          )}
        </div>
      )
    }

    const sharedControlProps = {
      id: inputId,
      name,
      disabled,
      onChange,
      onFocus: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFocused(true)
        onFocus?.(event as React.FocusEvent<HTMLInputElement>)
      },
      onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFocused(false)
        onBlur?.(event as React.FocusEvent<HTMLInputElement>)
      },
      placeholder: label ? " " : placeholder,
      value,
      ...inputProps,
      ...props,
    }

    return (
      <div className={cn("flex flex-col", marginClass, fullWidth && "w-full")} style={style}>
        <div className={sharedFieldClasses}>
          {InputProps?.startAdornment && (
            <div className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-[var(--text-muted)]">
              {InputProps.startAdornment}
            </div>
          )}

          {label && (
            <label htmlFor={inputId} className={labelClasses}>
              {label}
              {required ? " *" : ""}
            </label>
          )}

          {multiline ? (
            <textarea
              ref={ref as React.ForwardedRef<HTMLTextAreaElement>}
              rows={rows}
              className={cn(
                "min-h-[56px] w-full resize-y bg-transparent text-sm text-[var(--control-text)] outline-none placeholder:text-[var(--control-placeholder)]",
                getInputPadding(size, Boolean(label), hasStartAdornment, hasEndAdornment, true)
              )}
              style={{
                minHeight: minRows ? `${minRows * 24}px` : undefined,
                maxHeight: maxRows ? `${maxRows * 24}px` : undefined,
              }}
              {...(sharedControlProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            />
          ) : (
            <input
              ref={ref}
              type={type}
              className={cn(
                "h-14 w-full bg-transparent text-sm text-[var(--control-text)] outline-none placeholder:text-[var(--control-placeholder)]",
                size === "small" && "h-11",
                getInputPadding(size, Boolean(label), hasStartAdornment, hasEndAdornment)
              )}
              {...(sharedControlProps as React.InputHTMLAttributes<HTMLInputElement>)}
            />
          )}

          {InputProps?.endAdornment && (
            <div className="absolute right-4 top-1/2 z-10 -translate-y-1/2 text-[var(--text-muted)]">
              {InputProps.endAdornment}
            </div>
          )}
        </div>

        {helperText && (
          <p
              className={cn("mt-1.5 text-xs", error ? "text-[var(--text-danger)]" : "text-[var(--text-muted)]")}
            {...FormHelperTextProps}
          >
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

TextField.displayName = "TextField"
